import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, CheckSquare, Plus, ArrowLeft, Clock, Pencil, X, Download, Check, Trash2, Search } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const TasksPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ title: '', description: '', due_date: '', status: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    member_id: '',
    due_date: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/auth'); return; }
    fetchTasks();
    fetchMembers();
  }, [token]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, { headers });
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/members`, { headers });
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData, assigned_to: formData.assigned_to || user.id };
      await axios.post(`${API}/tasks`, submitData, { headers });
      setShowForm(false);
      setFormData({ title: '', description: '', assigned_to: '', member_id: '', due_date: '' });
      fetchTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}/status?status=${newStatus}`, {}, { headers });
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task status:', error);
    }
  };

  const startEdit = (task) => {
    setEditingId(task.id);
    setEditData({
      title: task.title,
      description: task.description || '',
      due_date: task.due_date,
      status: task.status
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({ title: '', description: '', due_date: '', status: '' });
  };

  const saveEdit = async (taskId) => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/tasks/${taskId}`, editData, { headers });
      setEditingId(null);
      fetchTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSavingEdit(false);
    }
  };

  const downloadPdf = async (taskId) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/pdf`, {
        headers,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `tache-${taskId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('PDF download failed:', error);
      window.open(`${API}/tasks/${taskId}/pdf?token=${token}`, '_blank');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Supprimer cette tâche ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { headers });
      fetchTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getMemberName = (memberId) => {
    if (!memberId) return null;
    const member = members.find(m => m.id === memberId);
    return member ? `${member.first_name} ${member.last_name}` : null;
  };

  const pendingTasks = tasks.filter(t => {
    if (t.status !== 'pending') return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return t.title.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q) || (getMemberName(t.member_id) || '').toLowerCase().includes(q);
  });
  const completedTasks = tasks.filter(t => {
    if (t.status !== 'completed') return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return t.title.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q) || (getMemberName(t.member_id) || '').toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Heart className="w-12 h-12 text-primary fill-primary mx-auto mb-4 animate-pulse" />
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const renderTaskCard = (task, isPending) => {
    const isEditing = editingId === task.id;

    return (
      <motion.div
        key={task.id}
        initial={{ opacity: 0, x: isPending ? -20 : 20 }}
        animate={{ opacity: 1, x: 0 }}
      >
        <Card data-testid={`task-card-${task.id}`} className={!isPending ? 'opacity-75' : ''}>
          <CardContent className="p-4 md:p-6">
            {isEditing ? (
              <div className="space-y-3">
                <Input
                  data-testid={`edit-task-title-${task.id}`}
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  placeholder="Titre"
                />
                <Textarea
                  data-testid={`edit-task-desc-${task.id}`}
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  placeholder="Description"
                  rows={3}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    data-testid={`edit-task-date-${task.id}`}
                    type="date"
                    value={editData.due_date}
                    onChange={(e) => setEditData({ ...editData, due_date: e.target.value })}
                  />
                  <select
                    data-testid={`edit-task-status-${task.id}`}
                    value={editData.status}
                    onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                    className="flex w-full rounded-xl border-slate-200 bg-white px-3 py-2 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                  >
                    <option value="pending">En attente</option>
                    <option value="completed">Terminée</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`save-edit-task-${task.id}`}
                    size="sm"
                    onClick={() => saveEdit(task.id)}
                    disabled={savingEdit}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    {savingEdit ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  <Button
                    data-testid={`cancel-edit-task-${task.id}`}
                    size="sm"
                    variant="outline"
                    onClick={cancelEdit}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between mb-3">
                  <h3 className={`text-lg font-bold flex-1 ${isPending ? 'text-primary-900' : 'text-slate-600 line-through'}`}>
                    {task.title}
                  </h3>
                  <div className="flex items-center gap-1 ml-2 shrink-0">
                    <button
                      data-testid={`edit-task-btn-${task.id}`}
                      onClick={() => startEdit(task)}
                      className="p-1.5 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <Pencil className="w-4 h-4 text-slate-400 hover:text-primary" />
                    </button>
                    <button
                      data-testid={`download-task-pdf-${task.id}`}
                      onClick={() => downloadPdf(task.id)}
                      className="p-1.5 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Télécharger PDF"
                    >
                      <Download className="w-4 h-4 text-slate-400 hover:text-primary" />
                    </button>
                    <button
                      data-testid={`delete-task-${task.id}`}
                      onClick={() => deleteTask(task.id)}
                      className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                    </button>
                    {isPending && (
                      <button
                        data-testid={`complete-task-${task.id}`}
                        onClick={() => handleStatusChange(task.id, 'completed')}
                        className="p-1.5 hover:bg-secondary-50 rounded-lg transition-colors"
                        title="Marquer comme terminé"
                      >
                        <CheckSquare className="w-4 h-4 text-slate-400 hover:text-secondary" />
                      </button>
                    )}
                  </div>
                </div>
                {task.description && (
                  <p className={`text-sm mb-3 ${isPending ? 'text-slate-600' : 'text-slate-500'}`}>{task.description}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {task.due_date}
                  </div>
                  {getMemberName(task.member_id) && (
                    <div className="px-2 py-1 bg-primary-50 text-primary rounded-lg text-xs font-medium">
                      {getMemberName(task.member_id)}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <button
              data-testid="back-to-dashboard"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-600 hover:text-primary mb-3 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au tableau de bord
            </button>
            <h1 className="text-2xl md:text-3xl font-bold text-primary-900 font-heading">
              Tâches
            </h1>
          </div>
          <Button
            data-testid="add-task-btn"
            onClick={() => setShowForm(!showForm)}
            className="w-full sm:w-auto"
          >
            <Plus className="w-5 h-5 mr-2" />
            {showForm ? 'Annuler' : 'Nouvelle tâche'}
          </Button>
        </div>

        {!showForm && tasks.length > 0 && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="task-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par titre, description, bénéficiaire..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
            />
          </div>
        )}

        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Card>
              <CardHeader>
                <CardTitle>Nouvelle tâche</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Titre</label>
                    <Input
                      data-testid="task-title-input"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Ex: Appeler la famille de..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                    <Textarea
                      data-testid="task-description-input"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Détails de la tâche..."
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Bénéficiaire concerné (optionnel)</label>
                      <select
                        data-testid="task-member-select"
                        name="member_id"
                        value={formData.member_id}
                        onChange={handleChange}
                        className="flex w-full rounded-xl border-slate-200 bg-white px-4 py-3 text-base transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                      >
                        <option value="">Aucun bénéficiaire spécifique</option>
                        {members.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.first_name} {member.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Date d'échéance</label>
                      <Input
                        data-testid="task-duedate-input"
                        type="date"
                        name="due_date"
                        value={formData.due_date}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <Button data-testid="submit-task-btn" type="submit" className="w-full">
                    Créer la tâche
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-warning" />
              En attente ({pendingTasks.length})
            </h2>
            <div className="space-y-4">
              {pendingTasks.length === 0 ? (
                <Card><CardContent className="p-8 text-center"><p className="text-slate-600">Aucune tâche en attente</p></CardContent></Card>
              ) : (
                pendingTasks.map((task) => renderTaskCard(task, true))
              )}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold text-primary-900 mb-4 flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-secondary" />
              Terminées ({completedTasks.length})
            </h2>
            <div className="space-y-4">
              {completedTasks.length === 0 ? (
                <Card><CardContent className="p-8 text-center"><p className="text-slate-600">Aucune tâche terminée</p></CardContent></Card>
              ) : (
                completedTasks.map((task) => renderTaskCard(task, false))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TasksPage;
